import React, { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Form, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRedo, faTrash } from "@fortawesome/free-solid-svg-icons";
import GenericModal from "src/components/global/GenericModal/GenericModal";
import { api_usps_subscriptions } from "src/api/api_usps_subscriptions";

export type USPSTrackingSubscription = {
  subscription_id: string;
  mid: string;
  listener_url: string | null;
  status: string;
  expiration_at: string | null;
  created_at: string;
  updated_at: string;
};

const extract_error = (err: any): string =>
  err?.response?.data?.msg ?? err?.message ?? "Unexpected error";

const STATUS_TYPES: Record<string, string> = {
  ENABLED: "success",
  SUSPENDED: "warning",
  DISABLED: "secondary",
};

const MID_FORMAT = /^\d{6}$|^\d{9}$/;

export default function SpitKitUSPSSubscriptionPanel() {
  const [subscriptions, setSubscriptions] = useState<
    USPSTrackingSubscription[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [midInput, setMidInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{
    subscription_id: string;
    mid: string;
  } | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    setWarningMessage(null);
    api_usps_subscriptions.get_all(
      (r: any) => {
        setSubscriptions(r.data.data);
        setLoading(false);
      },
      (err: any) => {
        if (err?.response?.data?.msg === "USPS integration is not configured") {
          setWarningMessage("USPS integration is not configured for this environment.");
        } else {
          setError(extract_error(err));
        }
        setLoading(false);
      },
    );
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const trimmedMid = midInput.trim();
  const midIsValid = MID_FORMAT.test(trimmedMid);

  const handleAdd = () => {
    if (!midIsValid) return;
    setSubmitting(true);
    setSubmitError(null);
    api_usps_subscriptions.subscribe(
      () => {
        setShowAddModal(false);
        setMidInput("");
        setSubmitting(false);
        showToast(`MID ${trimmedMid} subscribed successfully`);
        load();
      },
      (err: any) => {
        setSubmitError(extract_error(err));
        setSubmitting(false);
      },
      trimmedMid,
    );
  };

  const handleRenew = (mid: string) => {
    setError(null);
    setLoading(true);
    api_usps_subscriptions.subscribe(
      () => {
        showToast(`MID ${mid} renewed successfully`);
        load();
      },
      (err: any) => {
        setError(extract_error(err));
        setLoading(false);
      },
      mid,
    );
  };

  const handleDeleteConfirmed = () => {
    if (!confirmDelete) return;
    const { subscription_id, mid } = confirmDelete;
    setConfirmDelete(null);
    setError(null);
    setLoading(true);
    api_usps_subscriptions.unsubscribe(
      () => {
        showToast(`MID ${mid} removed successfully`);
        load();
      },
      (err: any) => {
        setError(extract_error(err));
        setLoading(false);
      },
      subscription_id,
    );
  };

  const handleCancel = () => {
    setShowAddModal(false);
    setMidInput("");
    setSubmitError(null);
  };

  return (
    <>
      {toast && (
        <Alert variant="success" className="py-2 small mb-2">
          {toast}
        </Alert>
      )}
      <GenericModal
        show={!!confirmDelete}
        title="Remove Subscription"
        canProceed
        canCancel
        proceeding={false}
        onCancel={() => setConfirmDelete(null)}
        onContinue={handleDeleteConfirmed}
      >
        <p className="mb-0 small">
          Remove subscription for MID <strong>{confirmDelete?.mid}</strong>?
          This will also delete it from USPS.
        </p>
      </GenericModal>

      <GenericModal
        show={showAddModal}
        title="Add MID Subscription"
        canProceed={midIsValid && !submitting}
        canCancel={!submitting}
        proceeding={submitting}
        onCancel={handleCancel}
        onContinue={handleAdd}
      >
        <>
          {submitError && <Alert variant="danger">{submitError}</Alert>}
          <Form.Group>
            <Form.Label>Mailer ID (MID)</Form.Label>
            <Form.Control
              type="text"
              value={midInput}
              onChange={(e) => setMidInput(e.target.value)}
              placeholder="6 or 9 digit MID"
              autoFocus
              isInvalid={trimmedMid.length > 0 && !midIsValid}
            />
            <Form.Control.Feedback type="invalid">
              MID must be 6 or 9 digits.
            </Form.Control.Feedback>
          </Form.Group>
        </>
      </GenericModal>

      <div className="d-flex align-items-center mb-2">
        <strong className="mr-auto">USPS MID Subscriptions</strong>
        {!warningMessage && (
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setShowAddModal(true)}
          >
            Add MID Subscription
          </Button>
        )}
      </div>

      {warningMessage && (
        <Alert variant="warning" className="small mb-0">
          {warningMessage}
        </Alert>
      )}

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center py-2">
          <Spinner animation="border" size="sm" />
        </div>
      ) : error || warningMessage ? null : (
        <table className="table table-sm table-bordered mb-0 small">
          <thead className="thead-light">
            <tr>
              <th>MID</th>
              <th>Status</th>
              <th>Expiration</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {subscriptions.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center text-muted">
                  No subscriptions
                </td>
              </tr>
            )}
            {subscriptions.map((sub) => (
              <tr key={sub.subscription_id}>
                <td className="font-monospace">{sub.mid}</td>
                <td>
                  <Badge variant={STATUS_TYPES[sub.status] ?? "secondary"}>
                    {sub.status}
                  </Badge>
                </td>
                <td>
                  {sub.expiration_at
                    ? new Date(sub.expiration_at).toLocaleDateString()
                    : "—"}
                </td>
                <td className="text-right">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="d-inline-flex align-items-center mr-1 py-0 px-1"
                    onClick={() => handleRenew(sub.mid)}
                  >
                    <FontAwesomeIcon icon={faRedo} className="mr-1" />
                    <span className="small">Renew</span>
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="d-inline-flex align-items-center py-0 px-1"
                    onClick={() =>
                      setConfirmDelete({
                        subscription_id: sub.subscription_id,
                        mid: sub.mid,
                      })
                    }
                  >
                    <FontAwesomeIcon icon={faTrash} className="mr-1" />
                    <span className="small">Delete</span>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
